import { AnimationMixer, LoopOnce, LoopRepeat } from "three";
import { serverTimeService } from "@modules/rosbag/ServerTimeService";
export class AnimationPlayback {
    get isPlaying() {
        if (this.selectedClip === null)
            return false;
        return this.mixer.clipAction(this.clips[this.selectedClip]).isRunning();
    }
    constructor(root, clips, clipIds = clips.map((_clip, index) => `clip:${index}`), clipOccurrences = clips.map((clip, index) => clips.slice(0, index).filter((previous) => previous.name === clip.name).length)) {
        this.root = root;
        this.clips = clips;
        this.clipIds = clipIds;
        this.clipOccurrences = clipOccurrences;
        this.loop = true;
        this.mixer = new AnimationMixer(root);
        this.selectedClip = clips.length > 0 ? 0 : null;
        this.selectClip(this.selectedClip);
    }
    get clipInfo() {
        return this.clips.map((clip, index) => ({
            key: this.clipIds[index], name: clip.name, occurrence: this.clipOccurrences[index], duration: clip.duration
        }));
    }
    applyState(state) {
        const index = state.clipKey === null ? -1 : this.clipIds.indexOf(state.clipKey);
        const selected = index < 0 ? null : index;
        const restart = !this.state || this.state.playbackId !== state.playbackId || this.selectedClip !== selected;
        this.setLoop(state.loop);
        if (restart)
            this.selectClip(selected);
        this.state = state;
        this.update(0);
    }
    get time() {
        return this.selectedClip === null ? 0 : this.mixer.clipAction(this.clips[this.selectedClip]).time;
    }
    selectClip(index, time = 0) {
        if (index !== null && (!Number.isInteger(index) || index < 0 || index >= this.clips.length)) {
            throw new Error(`[mesh] Invalid animation clip index: ${index}`);
        }
        this.mixer.stopAllAction();
        this.selectedClip = index;
        if (index !== null) {
            const action = this.mixer.clipAction(this.clips[index]).reset().play();
            action.setLoop(this.loop ? LoopRepeat : LoopOnce, this.loop ? Infinity : 1);
            action.clampWhenFinished = true;
            action.time = time;
        }
        this.mixer.update(0);
    }
    play() {
        if (this.selectedClip === null)
            return;
        const action = this.mixer.clipAction(this.clips[this.selectedClip]);
        if (!this.loop && action.time >= this.clips[this.selectedClip].duration)
            action.reset();
        action.paused = false;
        action.play();
    }
    pause() {
        if (this.state) {
            this.state = Object.assign(Object.assign({}, this.state), { position: this.time, playing: false });
        }
        if (this.selectedClip !== null)
            this.mixer.clipAction(this.clips[this.selectedClip]).paused = true;
    }
    reset() {
        if (this.selectedClip === null)
            return;
        this.selectClip(this.selectedClip);
        this.pause();
    }
    setLoop(loop) {
        this.loop = loop;
        if (this.selectedClip === null)
            return;
        const action = this.mixer.clipAction(this.clips[this.selectedClip]);
        action.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1);
        action.clampWhenFinished = true;
    }
    update(delta) {
        if (this.state && this.selectedClip !== null) {
            const state = this.state;
            const elapsed = state.playing ? Math.max(0, (serverTimeService.getServerTime() - state.updatedAt) / 1000) : 0;
            const position = state.position + elapsed;
            const action = this.mixer.clipAction(this.clips[this.selectedClip]);
            action.enabled = true;
            action.paused = false;
            action.time = state.loop && state.duration > 0 ? position % state.duration : Math.min(position, state.duration);
            this.mixer.update(0);
            action.paused = !state.playing || (!state.loop && position >= state.duration);
            return;
        }
        if (this.state)
            return;
        this.mixer.update(delta);
    }
    dispose() {
        this.mixer.stopAllAction();
        this.mixer.uncacheRoot(this.root);
    }
}
